// utils/dueTimeValidation.js
// Frontend counterpart of functions/dueTimeNormalization.js — kept as an
// intentional, documented duplicate (src/ and functions/ are separate
// Node projects with no shared module resolution here) rather than a
// shared package, matching this codebase's existing frontend/backend
// parity pattern (see notificationOrchestrator.js's combineDateTime vs
// utils/firmassistTime.js's combineAccraDateTime).
//
// Canonical contract: tasks.dueTime must be "HH:MM" (24-hour, Africa/Accra
// wall-clock — see utils/firmassistTime.js's combineAccraDateTime, which
// requires exactly this shape). A well-formed legacy 12-hour string
// ("3:00 PM") is deterministically normalized; anything else ambiguous or
// malformed is rejected, never guessed at.

const CANONICAL_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;
const LEGACY_12H_RE = /^(0?[1-9]|1[0-2]):([0-5]\d)\s*([AaPp][Mm])$/;

export const DUE_TIME_CLASS = Object.freeze({
  CANONICAL: 'canonical',
  LEGACY_12H: 'legacy-12h',
  BLANK: 'blank',
  MALFORMED: 'malformed',
});

export function classifyDueTime(dueTime) {
  if (dueTime === null || dueTime === undefined) return DUE_TIME_CLASS.BLANK;
  if (typeof dueTime !== 'string') return DUE_TIME_CLASS.MALFORMED;
  const trimmed = dueTime.trim();
  if (trimmed === '') return DUE_TIME_CLASS.BLANK;
  if (CANONICAL_RE.test(trimmed)) return DUE_TIME_CLASS.CANONICAL;
  if (LEGACY_12H_RE.test(trimmed)) return DUE_TIME_CLASS.LEGACY_12H;
  return DUE_TIME_CLASS.MALFORMED;
}

export function isCanonicalDueTime(value) {
  return classifyDueTime(value) === DUE_TIME_CLASS.CANONICAL;
}

export function normalizeLegacyDueTime(dueTime) {
  if (typeof dueTime !== 'string') return null;
  const trimmed = dueTime.trim();
  const m = trimmed.match(LEGACY_12H_RE);
  if (!m) return null;
  let hour = parseInt(m[1], 10);
  const minute = m[2];
  const meridiem = m[3].toLowerCase();
  if (meridiem === 'am') {
    if (hour === 12) hour = 0;
  } else if (hour !== 12) {
    hour += 12;
  }
  return `${String(hour).padStart(2, '0')}:${minute}`;
}

/**
 * The single write-boundary enforcement decision — see
 * functions/dueTimeNormalization.js's enforceCanonicalDueTime for the
 * full contract (identical here): {ok:true, value} to persist, or
 * {ok:false, error} with a short, user-facing message — never persist on
 * the false path, never guess.
 */
export function enforceCanonicalDueTime(rawValue) {
  const cls = classifyDueTime(rawValue);
  if (cls === DUE_TIME_CLASS.BLANK) return { ok: true, value: null };
  if (cls === DUE_TIME_CLASS.CANONICAL) return { ok: true, value: rawValue.trim() };
  if (cls === DUE_TIME_CLASS.LEGACY_12H) return { ok: true, value: normalizeLegacyDueTime(rawValue) };
  return { ok: false, error: 'Enter a valid time.' };
}
